import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { ArrowLeftIcon, CalendarIcon, CheckCircle2Icon, ClockIcon, MapPinIcon, UsersIcon } from 'lucide-react';
import { getEventBySlug } from '../data/events';
import { RegistrationForm } from '../components/RegistrationForm';
import { ETicket } from '../components/ETicket';
import { EmailPreview } from '../components/EmailPreview';
import { Registration } from '../types';
export function EventDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const event = slug ? getEventBySlug(slug) : undefined;
  const [submitted, setSubmitted] = useState<Registration | null>(null);
  if (!event) {
    return <main className="mx-auto w-full max-w-content px-5 py-32 text-center sm:px-8" data-id="element-324" data-mp="16u9sl5zlcsrk">
        <h1 className="font-display text-3xl font-semibold text-ink" data-id="element-325" data-mp="2w7n4phdy3l38">We could not find that event</h1>
        <p className="mt-3 text-sm text-ink-muted" data-id="element-326" data-mp="315f56zsnofj1">It may have passed, or the link may be incomplete.</p>
        <Link to="/" className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-signal" data-id="element-327" data-mp="21u96t9btmmnh">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-328" data-mp="1wuwisl97r9jb" />
          Back to upcoming events
        </Link>
      </main>;
  }
  const eventDate = new Date(event.date);
  return <main className="w-full bg-paper" data-id="element-329" data-mp="26nlrx9hakbbh">
      <section className="relative overflow-hidden bg-ink" data-id="element-330" data-mp="1iwy5zkajjl5x">
        <img src={event.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" data-id="element-331" data-mp="1ixhpv6jr4jml" />
        <div className="relative mx-auto max-w-content px-5 py-20 sm:px-8 sm:py-28" data-id="element-332" data-mp="1e07r30ah2vm3">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-white/70 transition-colors hover:text-white" data-id="element-333" data-mp="1rzh3rpzgz7lb">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-334" data-mp="33cjjyjxnpv4y" />
            All events
          </Link>
          <p className="eyebrow mt-8 text-signal-light" data-id="element-335" data-mp="2yerj30f1ayom">{event.format}</p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.08] text-white sm:text-5xl" data-id="element-336" data-mp="1w0u07tqspsdz">
            {event.title}
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-white/75 sm:text-lg" data-id="element-337" data-mp="20y3nb2qppryn">{event.strapline}</p>

          <dl className="mt-10 grid gap-6 border-t border-white/15 pt-8 sm:grid-cols-2 lg:grid-cols-4" data-id="element-338" data-mp="24z5o3o0a2c2q">
            {[{
            icon: CalendarIcon,
            label: 'Date',
            value: format(eventDate, 'EEEE d MMMM yyyy')
          }, {
            icon: ClockIcon,
            label: 'Time',
            value: event.timeRange
          }, {
            icon: MapPinIcon,
            label: 'Venue',
            value: `${event.venue}, ${event.city}`
          }, {
            icon: UsersIcon,
            label: 'Capacity',
            value: `${event.capacity} places, by review`
          }].map(item => <div key={item.label} className="flex gap-3" data-id="element-339" data-mp="1r3bm7oxx8mz4">
                <item.icon className="mt-0.5 h-4 w-4 shrink-0 text-signal-light" aria-hidden="true" data-id="element-340" data-mp="tlk0w8dzakju" />
                <div data-id="element-341" data-mp="yjli0nrug9so">
                  <dt className="eyebrow text-white/50" data-id="element-342" data-mp="yj3ct1n7el8m">{item.label}</dt>
                  <dd className="mt-1 text-sm text-white" data-id="element-343" data-mp="r15778857uao">{item.value}</dd>
                </div>
              </div>)}
          </dl>

          <a href="#register" className="mt-10 inline-flex items-center gap-2 rounded-full bg-signal px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-signal-deep lg:hidden" data-id="element-344" data-mp="h3hbaeyswgqx">
            Register to attend
          </a>
        </div>
      </section>

      <div className="mx-auto grid max-w-content gap-14 px-5 py-16 sm:px-8 lg:grid-cols-[1.35fr_1fr] lg:gap-20" data-id="element-345" data-mp="3c2py1hf83gp">
        <div className="space-y-16" data-id="element-346" data-mp="3uhui16erdq65">
          <section data-id="element-347" data-mp="3uix5138yn8gs">
            <h2 className="font-display text-2xl font-semibold text-ink" data-id="element-348" data-mp="3ugb0lchd992s">About this session</h2>
            <p className="mt-4 text-base leading-relaxed text-ink-soft" data-id="element-349" data-mp="3ph7tlmhtsss6">{event.description}</p>
            <p className="mt-4 text-sm leading-relaxed text-ink-muted" data-id="element-350" data-mp="3lgwd8lwhxemi">
              <span className="text-ink" data-id="element-351" data-mp="3gjxic150d3n9">Who is in the room:</span> {event.audience}.
            </p>
            <ul className="mt-6 flex flex-wrap gap-2" data-id="element-352" data-mp="1kgrdspsz7liq">
              {event.themes.map(theme => <li key={theme} className="rounded-full border border-paper-line bg-white px-3.5 py-1.5 text-xs text-ink-muted" data-id="element-353" data-mp="1ofrvg4zpwi45">
                  {theme}
                </li>)}
            </ul>
          </section>

          <section data-id="element-354" data-mp="10r7fi9ailvoy">
            <h2 className="font-display text-2xl font-semibold text-ink" data-id="element-355" data-mp="10pxhfk3nwnib">Agenda</h2>
            <ol className="mt-6 border-t border-paper-line" data-id="element-356" data-mp="15rms2ymz2z5v">
              {event.agenda.map(item => <li key={`${item.time}${item.title}`} className="grid gap-2 border-b border-paper-line py-5 sm:grid-cols-[5rem_1fr] sm:gap-6" data-id="element-357" data-mp="rs3x77j69kbi">
                  <span className="font-mono text-sm text-signal" data-id="element-358" data-mp="2m64x7vqgkdd6">{item.time}</span>
                  <div data-id="element-359" data-mp="2r46c04gupqiu">
                    <h3 className="text-base font-medium text-ink" data-id="element-360" data-mp="2r3ejx3mgqaxb">{item.title}</h3>
                    {item.speaker && <p className="mt-1 text-sm text-ink-muted" data-id="element-361" data-mp="318czx4pq6qeb">{item.speaker}</p>}
                    {item.description && <p className="mt-1.5 text-sm leading-relaxed text-ink-muted" data-id="element-362" data-mp="2w6xhzliuh005">{item.description}</p>}
                  </div>
                </li>)}
            </ol>
          </section>

          <section data-id="element-363" data-mp="3jy2w9w94zk8c">
            <h2 className="font-display text-2xl font-semibold text-ink" data-id="element-364" data-mp="3jxjlr0ltfy4c">Speakers</h2>
            <ul className="mt-6 grid gap-4 sm:grid-cols-2" data-id="element-365" data-mp="3ezrnloukvfrp">
              {event.speakers.map(speaker => <li key={speaker.name} className="rounded-xl border border-paper-line bg-white p-5" data-id="element-366" data-mp="3szaqa24mdywm">
                  <p className="font-display text-lg font-semibold text-ink" data-id="element-367" data-mp="3oyh5viqdz28h">{speaker.name}</p>
                  <p className="mt-1 text-sm text-ink-soft" data-id="element-368" data-mp="1yk8a4v1gnt6d">{speaker.role}</p>
                  <p className="mt-0.5 text-sm text-ink-muted" data-id="element-369" data-mp="1ujobto55ijin">{speaker.organisation}</p>
                </li>)}
            </ul>
          </section>

          {event.partners.length > 0 && <section data-id="element-370" data-mp="2hbu12utsa5f">
              <h2 className="font-display text-2xl font-semibold text-ink" data-id="element-371" data-mp="2iwoytl1xv3t">In partnership with</h2>
              <ul className="mt-5 flex flex-wrap gap-x-8 gap-y-3 text-sm text-ink-muted" data-id="element-372" data-mp="7fwtv1660rtc">
                {event.partners.map(partner => <li key={partner} data-id="element-373" data-mp="cdonbzmgklzn">{partner}</li>)}
              </ul>
            </section>}
        </div>

        <aside id="register" className="lg:sticky lg:top-24 lg:self-start" data-id="element-374" data-mp="lg7mo9r1cgh5">
          {submitted ? <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.4
        }} className="space-y-6" data-id="element-375" data-mp="q6wig19qzzm3">
              <div className="rounded-xl border border-signal/25 bg-white p-6" data-id="element-376" data-mp="106nazxer04jj">
                <div className="flex items-start gap-3" data-id="element-377" data-mp="146pd27oeazzc">
                  <CheckCircle2Icon className="mt-0.5 h-5 w-5 shrink-0 text-signal" aria-hidden="true" data-id="element-378" data-mp="1454k2mhqbp3l" />
                  <div data-id="element-379" data-mp="2on5d8ri6j7zl">
                    <h2 className="font-display text-xl font-semibold text-ink" data-id="element-380" data-mp="2knwflbk0qpyr">Registration received</h2>
                    <p className="mt-2 text-sm leading-relaxed text-ink-muted" data-id="element-381" data-mp="2fo1pvbbzc6fa">
                      Thank you, {submitted.fullName.split(' ')[0]}. We have emailed{' '}
                      <span className="text-ink" data-id="element-382" data-mp="2artfeb8q91eg">{submitted.email}</span> to confirm receipt. Human Planet will review
                      your registration, usually within two working days, and your ticket will arrive as a PDF once
                      approved.
                    </p>
                    <p className="mt-4 font-mono text-xs text-ink-muted" data-id="element-383" data-mp="2ylcp4vxm2zuq">
                      Reference {submitted.ticketCode}, pending review
                    </p>
                  </div>
                </div>
                <button type="button" onClick={() => setSubmitted(null)} className="mt-5 text-sm font-medium text-signal underline decoration-signal/30 underline-offset-4 hover:decoration-signal" data-id="element-384" data-mp="3r5joxxim8hvh">
                  Register someone else
                </button>
              </div>

              <div data-id="element-385" data-mp="2lq20yjlgpf8i">
                <h3 className="eyebrow text-ink-muted" data-id="element-386" data-mp="2lqlku5uoadp6">Your ticket, once approved</h3>
                <div className="mt-3" data-id="element-387" data-mp="2pqvp9e2tgw1x">
                  <ETicket event={event} registration={submitted} state="pending" data-id="element-388" data-mp="3dfx35guf4zel" />
                </div>
              </div>

              <div data-id="element-389" data-mp="3ifjetlulm7la">
                <h3 className="eyebrow text-ink-muted" data-id="element-390" data-mp="3idyhjogwgajq">The email we just sent</h3>
                <div className="mt-3" data-id="element-391" data-mp="34g1mwxm0ae7p">
                  <EmailPreview kind="received" event={event} registration={submitted} sentAt={submitted.submittedAt} data-id="element-392" data-mp="34eywsrwicfsu" />
                </div>
              </div>
            </motion.div> : <div className="rounded-xl border border-paper-line bg-white p-6 sm:p-8" data-id="element-393" data-mp="1sys5egmftw91">
              <p className="eyebrow text-signal" data-id="element-394" data-mp="1y0r9jsq04x71">Registration</p>
              <h2 className="mt-3 font-display text-2xl font-semibold leading-snug text-ink" data-id="element-395" data-mp="1phmgu2mhqehx">Register to attend</h2>
              <p className="mt-2 text-sm leading-relaxed text-ink-muted" data-id="element-396" data-mp="1lfiw0ndxlxjz">
                {event.registrationOpen ? `${event.capacity} places. Every registration is reviewed by Human Planet before a ticket is issued.` : 'Registration for this event is currently closed.'}
              </p>
              <div className="mt-7" data-id="element-397" data-mp="16rxswth23yc2">
                {event.registrationOpen ? <RegistrationForm event={event} onSubmitted={setSubmitted} data-id="element-398" data-mp="11tw740s8xkww" /> : <p className="rounded-lg bg-paper-warm px-4 py-3 text-sm text-ink-muted" data-id="element-399" data-mp="1gi95t5ipxhv3">
                    Write to events@human-planet.com to join the waiting list.
                  </p>}
              </div>
            </div>}
        </aside>
      </div>
    </main>;
}